import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, ExportBar } from '../components/Filters';
import { ColFilter } from '../components/ColFilter';

// Removed TABS array as we are using independent combined filters

export default function IEC() {
    // Redirect to login page if DevTools or View Source shortcuts are pressed
    useEffect(() => {
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12') {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+Shift+I or Cmd+Opt+I
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+U or Cmd+U
        if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
          window.location.href = '/login';
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
  const { can } = useAuth();
  
  const getSaved = (k, d) => { try { const v = sessionStorage.getItem('iec_'+k); return v!==null ? JSON.parse(v) : d; } catch { return d; } };

  const [summary,  setSummary] = useState(null);
  const [records,  setRecords] = useState([]);
  const [total,    setTotal]   = useState(0);
  const [page,     setPage]    = useState(() => getSaved('page', 1));
  const [search,   setSearch]  = useState(() => getSaved('search', ''));
  const [dSearch,  setDSearch] = useState(() => getSaved('search', ''));
  const [loading,  setLoading] = useState(true);
  const [dateFilters, setDateFilters] = useState(() => getSaved('date', { fromDate:'', toDate:'' }));

  // Independent contact filters
    const [contactInfo, setContactInfo] = useState(() => getSaved('contactInfo', '')); // '', 'email', 'phone', 'both', 'either'

    // Column filters
    const [colStatus, setColStatus] = useState(() => getSaved('status', ''));

    useEffect(() => {
      sessionStorage.setItem('iec_page', JSON.stringify(page));
      sessionStorage.setItem('iec_search', JSON.stringify(search));
      sessionStorage.setItem('iec_date', JSON.stringify(dateFilters));
      sessionStorage.setItem('iec_contactInfo', JSON.stringify(contactInfo));
      sessionStorage.setItem('iec_status', JSON.stringify(colStatus));
    }, [page, search, dateFilters, contactInfo, colStatus]);
  const limit = 50;

  useEffect(() => { api.iec.summary().then(setSummary); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== dSearch) {
        setDSearch(search); setPage(1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, dSearch]);

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (dSearch) p.search = dSearch;
    if (dateFilters.fromDate) p.fromDate = dateFilters.fromDate;
    if (dateFilters.toDate)   p.toDate   = dateFilters.toDate;
    
      if (contactInfo) p.contactInfo = contactInfo;

      if (colStatus) p.status = colStatus; // MCA Status filter sent to server

      return p;
    }, [page, dSearch, dateFilters, contactInfo, colStatus]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.iec.records(buildParams());
      setRecords(res.rows);
      setTotal(res.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // Use records directly
  const filteredRows = records;

  useEffect(() => { setPage(1); }, [colStatus]);

  const handleExport = (fmt) => {
    const p = buildParams(); delete p.page; delete p.limit; p.format = fmt;
    api.iec.export(p);
  };

  const clearAll = () => {
    setSearch(''); setDSearch('');
    setDateFilters({ fromDate:'', toDate:'' });
    setColStatus('');
    setContactInfo('');
  };

  const hasFilters = dSearch || dateFilters.fromDate || dateFilters.toDate || colStatus || contactInfo;
  const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-IN');
  const totalPages = Math.ceil(total / limit);

  // Dynamic options fetched from server summary
  const statusOpts = summary?.statuses
    ? summary.statuses.map(v => ({ value: v, label: v }))
    : [];

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <div>
          <h1 className="page-title">IEC / Import-Export Companies</h1>
          <p className="page-desc">Companies identified via SIMS portal as having Import-Export Code</p>
        </div>
        {hasFilters && (
          <button className="btn-clear-all" onClick={clearAll}>✕ Clear all filters</button>
        )}
      </div>

      {/* ── Summary cards ── */}
      {summary && (
          <div className="stat-grid" style={{ marginBottom: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <div className="stat-card stat-card--accent">
              <div className="stat-value">{fmt(summary.TOTAL_RECORDS)}</div>
              <div className="stat-label">IEC Companies</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#44BBA4' }}>{fmt(summary.WITH_EMAIL)}</div>
              <div className="stat-label">With Email</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#44BBA4' }}>{fmt(summary.WITH_MOBILE)}</div>
              <div className="stat-label">With Mobile</div>
            </div>
          </div>
      )}

      {/* ── Search + date filter ── */}
      <div className="filter-section" style={{ marginTop: '0.75rem' }}>
        <div className="filter-row">
          <input
            className="search-input"
            placeholder="Search by PAN, IEC name or company name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {dSearch && (
            <button className="search-clear" onClick={() => { setSearch(''); setDSearch(''); }}>✕</button>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' }}>
          <DateRangeFilter
            filters={dateFilters}
            onChange={f => { setDateFilters(f); setPage(1); }}
            label="EXTRACTION DATE"
          />

          <div className="filter-group">
            <label>Contact Info</label>
            <select value={contactInfo} onChange={e => { setContactInfo(e.target.value); setPage(1); }} className="form-select" style={{ maxWidth: '200px' }}>
              <option value="">All</option>
              <option value="email">Has Email</option>
              <option value="phone">Has Phone</option>
              <option value="both">Both Email & Phone</option>
              <option value="either">Either</option>
            </select>
          </div>
        </div>
      </div>

      <ExportBar onExport={handleExport} total={total} canExport={can('export')} />

      {/* ── Table ── */}
      <div className="table-card" style={{ marginTop: '12px' }}>
        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className="table-meta-bar">
              <span>
                {colStatus
                  ? <><strong>{filteredRows.length}</strong> of {fmt(total)} records (column filter active)</>
                  : <><strong>{fmt(total)}</strong> records · Page {page} of {totalPages || 1}</>}
              </span>
              {colStatus && (
                <button className="col-filter-reset" onClick={() => setColStatus('')}>
                  Reset column filters
                </button>
              )}
            </div>

            {filteredRows.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  {contactInfo === 'email' ? '✉️' :
                   contactInfo === 'phone' ? '📱' :
                   contactInfo === 'both'  ? '📞' : '🔍'}
                </div>
                <div className="empty-state-title">
                  {contactInfo === 'email'  ? 'No Companies with Email Found' :
                   contactInfo === 'phone'  ? 'No Companies with Phone Found' :
                   contactInfo === 'both'   ? 'No records matching both Email & Phone' :
                   contactInfo === 'either' ? 'No Contact Info Found' :
                   'No IEC Records Found'}
                </div>
                <div className="empty-state-desc">
                  {dSearch
                    ? `No records match "${dSearch}". Try a different PAN or company name.`
                    : 'No records available for the selected filters.'}
                </div>
                {hasFilters && <button className="empty-state-btn" onClick={clearAll}>Clear Filters</button>}
              </div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>PAN</th>
                      <th>IEC Name</th>
                      <th>Company Name (MCA)</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>
                        <ColFilter
                          label="MCA Status"
                          options={[
                            { value: 'Active',   label: '✓ Active'   },
                            { value: 'Inactive', label: '✕ Inactive' },
                          ]}
                          value={colStatus}
                          onChange={setColStatus}
                          width={160}
                        />
                      </th>
                      <th>Extracted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr key={`${r.PAN}-${i}`}>
                        <td className="mono">{r.PAN}</td>
                        <td className="td-company">{r.IEC_NAME || '—'}</td>
                        <td>{r.COMPANY_NAME || '—'}</td>
                        <td>
                          {r.IEC_MOBILE
                            ? <span className="contact-value">{r.IEC_MOBILE}</span>
                            : <span className="no-data">—</span>}
                        </td>
                        <td>
                          {r.IEC_EMAIL
                            ? <span className="contact-value small-text">{r.IEC_EMAIL}</span>
                            : <span className="no-data">—</span>}
                        </td>
                        <td>
                          <span className={r.COMPANY_STATUS === 'Active' ? 'badge-yes' : 'badge-no'}>
                            {r.COMPANY_STATUS || '—'}
                          </span>
                        </td>
                        <td className="date-cell">
                          {r.EXTRACTION_DATE ? new Date(r.EXTRACTION_DATE).toLocaleDateString('en-IN') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredRows.length > 0 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
                <span className="page-info">Page {page} / {totalPages || 1}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
